import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { StoreProduct } from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useMedia } from '@/features/media';
import {
  ScopeChip,
  SearchEnginesCard,
  type SearchEnginesTab,
  type SharedProductSections,
} from '@/features/products';
import {
  resolveSeoPreview,
  SeoOverrideFields,
  SeoPreviewCard,
  useSeoStoreContext,
} from '@/features/seo';

import type {
  StoreProductFormInput,
  StoreProductFormOutput,
} from '../schemas/store-product-form-schema';

export interface StoreSearchEnginesCardProps {
  storeId: string;
  row: StoreProduct;
  /** The master's cards; absent if the master could not be read. */
  shared?: SharedProductSections | undefined;
}

/**
 * This store's listing on search and answer engines, and the Summary and FAQ every store shares.
 * One card holds both: the listing writes this store, the answers write the master product, and
 * the save bar takes each to its own place. Blank SEO fields show the master's own, as the storefront
 * resolves them.
 */
export function StoreSearchEnginesCard({ storeId, row, shared }: StoreSearchEnginesCardProps) {
  const { t } = useTranslation(['storeCatalog', 'products']);
  const form = useFormContext<StoreProductFormInput, unknown, StoreProductFormOutput>();
  const seoStore = useSeoStoreContext(storeId);
  const master = row.product;
  const [title, handle, descriptionHtml, status, seo, featuredMediaId] = form.watch([
    'title',
    'handle',
    'descriptionHtml',
    'status',
    'seo',
    'featuredMediaId',
  ]);
  const featuredId = featuredMediaId ?? master.featuredMediaId;
  const featured = useMedia(featuredId);
  const path = `/products/${handle}`;
  const description = descriptionHtml || (master.descriptionHtml ?? master.summary);
  const live = seoStore
    ? resolveSeoPreview(
        {
          title: title || master.title,
          description,
          path,
          published: status === 'published',
          override: {
            title: seo.title || (master.seo?.title ?? ''),
            description: seo.description || (master.seo?.description ?? ''),
            canonical: seo.canonical || (master.seo?.canonical ?? ''),
            noIndex: seo.noIndex || Boolean(master.seo?.noIndex),
          },
          image: featuredId ? { alt: featured.data?.alt ?? null } : 'none',
        },
        seoStore,
      )
    : null;
  const ownSeo = Boolean(seo.title || seo.description || seo.canonical || seo.noIndex);
  const warnings = live?.warnings.length ?? 0;
  const { errors } = form.formState;
  const masterErrors = shared?.form.formState.errors;
  const errorTab: SearchEnginesTab | undefined =
    errors.handle || errors.seo
      ? 'listing'
      : masterErrors?.summary || masterErrors?.faq
        ? 'answers'
        : undefined;

  return (
    <SearchEnginesCard
      badge={<ScopeChip scope="store" />}
      meta={[
        handle !== master.handle ? t('storeCatalog:storePage.search.ownHandle') : null,
        ownSeo
          ? t('storeCatalog:storePage.search.ownSeo')
          : t('storeCatalog:storePage.search.inheritsSeo'),
        warnings > 0 ? t('products:searchCard.warnings', { count: warnings }) : null,
      ]}
      errorTab={errorTab}
      answersBadge={
        shared?.answersBadge ? <Badge variant="secondary">{shared.answersBadge}</Badge> : null
      }
      listing={
        <>
          <FormField
            control={form.control}
            name="handle"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('storeCatalog:fields.handle')}</FormLabel>
                <FormControl>
                  <Input {...field} className="font-mono text-[13px]" />
                </FormControl>
                <FormDescription>
                  {t('storeCatalog:storePage.search.handleHint', { master: master.handle })}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <SeoOverrideFields bare />
        </>
      }
      preview={<SeoPreviewCard live={live} saved={null} path={path} bare />}
      answers={
        shared?.answers ?? (
          <p className="text-muted-foreground text-[13px]">
            {t('products:searchCard.sharedAnswers')}
          </p>
        )
      }
    />
  );
}
